import { useCallback, useEffect, useState } from "react";
import { toast } from "@/components/ui/use-toast";
import { contactService } from "@/services/contactService";
import { ContactDTO } from "@/types/contact";

/**
 * The client's contacts, as edited on the bid page.
 *
 * Contacts belong to the client rather than the bid, but an admin edits them
 * while pricing the bid, so they are loaded and saved alongside it rather than
 * behind their own screen.
 */
export const useBidContacts = (eventId?: string) => {
  const [contacts, setContacts] = useState<ContactDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!eventId || eventId === "new") {
      return;
    }

    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const loaded = await contactService.listForEvent(eventId);
        if (active) {
          setContacts(Array.isArray(loaded) ? loaded : []);
        }
      } catch (error) {
        console.error("Error loading contacts:", error);
        // A contact list that fails to load should not block pricing the bid.
        if (active) {
          setContacts([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [eventId]);

  /**
   * Persists the list. Returns false when it could not be saved, so the caller
   * can decide whether to carry on with the rest of the save.
   */
  const saveContacts = useCallback(async (): Promise<boolean> => {
    if (!eventId || eventId === "new") {
      // A bid that does not exist yet has no client to hang contacts off.
      return true;
    }

    const named = contacts.filter((contact) => contact.name.trim() !== "");

    const unreachable = named.filter(
      (contact) =>
        (contact.receivesBid || contact.receivesInvoice) &&
        !contact.email?.trim(),
    );
    if (unreachable.length > 0) {
      toast({
        title: "Contact needs an email",
        description: `${unreachable[0].name.trim()} is set to receive the bid or invoice but has no email address.`,
        variant: "destructive",
      });
      return false;
    }

    try {
      const saved = await contactService.replaceForEvent(eventId, named);
      // Trust what we sent if the response carried no body; the list is saved
      // either way, and a missing body must not blank out the page.
      setContacts(Array.isArray(saved) ? saved : named);
      return true;
    } catch (error) {
      console.error("Error saving contacts:", error);
      toast({
        title: "Error",
        description: "Failed to save contacts",
        variant: "destructive",
      });
      return false;
    }
  }, [eventId, contacts]);

  return { contacts, setContacts, saveContacts, loading };
};
